<template>
  <LineChartGenerator
    v-if="chartData.datasets.length > 0 && chartOptions !== null"
    :id="chartId"
    :options="chartOptions"
    :data="chartData"
    :dataset-id-key="datasetIdKey"
    :plugins="plugins"
    :css-classes="cssClasses"
    :styles="styles"
    :width="width"
    :height="height"
  />
</template>

<script setup lang="ts">
import { Line as LineChartGenerator } from 'vue-chartjs'

import { getAxis, getChartOptions } from '~/utils/chart/multi-axis-chart'
import { numberFormatter } from '~/utils/number'

const props = withDefaults(
  defineProps<{
    chartId: string
    datasetIdKey: string
    width: number
    height: number
    cssClasses: string
    styles: Record<string, unknown>
    plugins: Array<unknown>
    chartData: Record<string, unknown>
    categories: Array<unknown>
    hasPriceAxis: boolean
    hasPayableAxis: boolean
    showPayableAxis: boolean
    showPriceAxis: boolean
    yAxisLabel: string
    yAxis1Label: string
    minY1: number
    maxY1: number | null
    minY2: number
    maxY2: number | null
    replaceLegend: boolean
    isDashedCrosshair?: boolean
    market?: string
  }>(),
  {
    isDashedCrosshair: false,
    chartId: 'line-chart',
    datasetIdKey: 'label',
    width: 400,
    height: 400,
    cssClasses: '',
    market: '',
    styles: () => ({}),
    plugins: () => [],
  },
)

const showTicks = computed(
  () =>
    !props.chartData.datasets.some((dataset) =>
      dataset.data.every((point) => point.y === null),
    ),
)

const blackMassAxis = computed(() => {
  const y2 = getAxis({
    title: props.yAxis1Label,
    min: props.minY2,
    max: props.maxY2,
    display: props.showPriceAxis,
    position: props.hasPriceAxis ? 'right' : 'left',
    drawOnChartArea: props.showPriceAxis,
    ticks: {
      display: showTicks.value,
      callback(value) {
        return value > 0 ? `${numberFormatter(value)}` : value
      },
    },
  })

  const y1 = getAxis({
    title: props.yAxisLabel,
    min: props.minY1,
    max: props.maxY1,
    display: props.showPayableAxis,
    drawOnChartArea: !props.showPriceAxis,
  })

  return { y1, y2 }
})

const normalAxis = computed(() => {
  const axis: any = {}

  if (props.hasPriceAxis) {
    axis.y1 = getAxis({
      title: props.yAxisLabel,
      min: props.minY1,
      max: props.maxY1,
      display: props.showPriceAxis,
      ticks: {
        display: showTicks.value,
        callback(value) {
          return value > 0 ? `${numberFormatter(value)}` : value
        },
      },
    })
  }

  if (props.hasPayableAxis) {
    axis.y2 = getAxis({
      title: props.yAxis1Label,
      min: props.minY2,
      max: props.maxY2,
      position: props.hasPriceAxis ? 'right' : 'left',
      display: props.showPayableAxis,
      drawOnChartArea: !props.showPriceAxis,
    })
  }

  return axis
})

const chartOptions = computed(() => {
  const isBlackMass = props.market === 'black-mass'

  const options = getChartOptions({
    categories: props.categories,
    replaceLegend: props.replaceLegend,
    dashPattern: props.isDashedCrosshair ? [2, 2] : [0, 0],
  })

  const axis = isBlackMass ? blackMassAxis.value : normalAxis.value

  return {
    ...options,
    scales: {
      ...options.scales,
      ...axis,
    },
  }
})
</script>
